// Ширина экрана для раскладки пульта (задача D5, ui-spec §6). Раскладку решает код, а не только CSS: на узком экране
// меняется не вид, а состав (сайдбар уходит в выезжающее меню, таблица становится списком), и дубли разметки
// «на все ширины сразу» путали бы чтение с экрана.
//
// | Ширина      | Вид       | Раскладка                                                     |
// |-------------|-----------|---------------------------------------------------------------|
// | ≥ 1280      | `wide`    | сайдбар 224 px с подписями                                    |
// | 1024–1279   | `compact` | сайдбар иконками, подписи — в тултипах; вторичные колонки скрыты |
// | 640–1023    | `tablet`  | меню выезжает («☰» в верхней полосе), фильтры — кнопкой      |
// | < 640       | `phone`   | одна колонка, таблицы — списком в две строки, касание ≥ 40 px |
import { useSyncExternalStore } from 'react';

export type Viewport = 'phone' | 'tablet' | 'compact' | 'wide';

const QUERIES = {
  tablet: '(min-width: 640px)',
  compact: '(min-width: 1024px)',
  wide: '(min-width: 1280px)',
} as const;

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function matches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

function viewportNow(): Viewport {
  if (matches(QUERIES.wide)) return 'wide';
  if (matches(QUERIES.compact)) return 'compact';
  if (matches(QUERIES.tablet)) return 'tablet';
  return 'phone';
}

function subscribeTo(queries: readonly string[]) {
  return (listener: () => void): (() => void) => {
    if (typeof window.matchMedia !== 'function') return () => {};
    const lists = queries.map((query) => window.matchMedia(query));
    for (const list of lists) list.addEventListener('change', listener);
    return () => {
      for (const list of lists) list.removeEventListener('change', listener);
    };
  };
}

const subscribeViewport = subscribeTo(Object.values(QUERIES));
const subscribeMotion = subscribeTo([REDUCED_MOTION]);

/** Вид экрана по ширине; перерисовка при пересечении границы. */
export function useViewport(): Viewport {
  return useSyncExternalStore(subscribeViewport, viewportNow, viewportNow);
}

/** Меню выезжает (нет сайдбара): уже 1024. */
export function isNarrow(viewport: Viewport): boolean {
  return viewport === 'phone' || viewport === 'tablet';
}

function reducedNow(): boolean {
  return matches(REDUCED_MOTION);
}

/** Просил ли человек меньше движения (`prefers-reduced-motion`). */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribeMotion, reducedNow, reducedNow);
}
